import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Download } from 'lucide-react';
import HRHeader, { getHRUser } from '@/components/HRHeader';
import { authHeaders, formatMinutes, formatTime, todayIST } from '@/lib/tracking';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REPORTS = {
  daily: {
    label: 'Daily summary',
    columns: [
      ['Date', (r) => r.date],
      ['Employee', (r) => `${r.name} (${r.employee_id})`],
      ['Department', (r) => r.department || '—'],
      ['Duty', (r) => `${formatTime(r.duty_start)} – ${r.duty_end ? formatTime(r.duty_end) : 'on duty'}`],
      ['Total duty', (r) => formatMinutes(r.duty_min)],
      ['Locations visited', (r) => r.locations_visited.join(', ') || '—'],
      ['Visit time', (r) => formatMinutes(r.visit_min)],
      ['Travel time', (r) => `${formatMinutes(r.travel_min)} · ${r.distance_km} km`],
      ['Tracking gaps', (r) => (r.gaps ? `${r.gaps} · ${formatMinutes(r.gap_min)}` : 'None')],
      ['Last location', (r) => r.last_location || '—'],
    ],
  },
  visits: {
    label: 'Location visits',
    columns: [
      ['Date', (r) => r.date],
      ['Employee', (r) => `${r.name} (${r.employee_id})`],
      ['Location', (r) => `${r.location}${r.assigned ? '' : ' (not assigned)'}`],
      ['Arrival', (r) => `${r.arrival_exact ? '' : 'before '}${formatTime(r.arrival)}`],
      ['Departure', (r) => `${r.departure_exact ? '' : 'after '}${formatTime(r.departure)}`],
      ['Duration', (r) => formatMinutes(r.duration_min)],
    ],
  },
  travel: {
    label: 'Travel',
    columns: [
      ['Date', (r) => r.date],
      ['Employee', (r) => `${r.name} (${r.employee_id})`],
      ['From', (r) => r.from],
      ['To', (r) => r.to],
      ['Departure', (r) => formatTime(r.departure)],
      ['Arrival', (r) => formatTime(r.arrival)],
      ['Duration', (r) => formatMinutes(r.duration_min)],
      ['Distance', (r) => `${r.distance_km} km`],
    ],
  },
};

export default function HRTrackingReports() {
  const navigate = useNavigate();
  const [user] = useState(getHRUser);
  const [params] = useSearchParams();
  const [type, setType] = useState('daily');
  const [dateFrom, setDateFrom] = useState(params.get('date') || todayIST());
  const [dateTo, setDateTo] = useState(params.get('date') || todayIST());
  const [employeeId, setEmployeeId] = useState(params.get('employee') || '');
  const [employees, setEmployees] = useState([]);
  // Rows are kept with the report they belong to, so switching tabs never renders old rows with new columns
  const [result, setResult] = useState({ type: null, rows: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/hr/login');
      return;
    }
    axios.get(`${API}/employees`, { headers: authHeaders() })
      .then((r) => setEmployees(r.data.filter((e) => e.field_tracking).sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => {});
  }, [user, navigate]);

  const query = () => ({ date_from: dateFrom, date_to: dateTo, ...(employeeId ? { employee_id: employeeId } : {}) });

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    axios.get(`${API}/tracking/reports/${type}`, { params: query(), headers: authHeaders() })
      .then((r) => setResult({ type, rows: r.data.rows }))
      .catch((e) => {
        setResult({ type, rows: null });
        toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not load report');
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, type, dateFrom, dateTo, employeeId]);

  const download = async () => {
    try {
      const r = await axios.get(`${API}/tracking/reports/${type}`, { params: { ...query(), format: 'csv' }, headers: authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `field_${type}_${dateFrom}_${dateTo}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  if (!user) return null;
  const report = REPORTS[type];
  const rows = result.type === type ? result.rows : null;

  return (
    <div className="min-h-screen bg-zinc-50">
      <HRHeader title="Field Tracking Reports" user={user} active="/hr/tracking" />
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="flex flex-wrap items-end gap-4">
          <button onClick={() => navigate('/hr/tracking')} className="h-10 px-3 flex items-center gap-1 text-sm font-bold uppercase text-zinc-700 hover:text-zinc-900">
            <ArrowLeft size={16} /> Live tracking
          </button>
          <div className="flex gap-2">
            {Object.entries(REPORTS).map(([key, r]) => (
              <button
                key={key}
                onClick={() => setType(key)}
                className={key === type ? 'h-10 px-4 bg-zinc-900 text-white text-sm font-bold uppercase rounded-sm' : 'h-10 px-4 bg-white border-2 border-zinc-200 text-sm font-bold uppercase rounded-sm'}
                data-testid={`report-${key}`}
              >{r.label}</button>
            ))}
          </div>
          <label className="text-sm">
            <span className="block text-zinc-500 font-medium mb-1 uppercase tracking-wider text-xs">From</span>
            <input type="date" value={dateFrom} max={todayIST()} onChange={(e) => setDateFrom(e.target.value)} className="h-10 px-3 border-2 border-zinc-200 rounded-sm bg-white" />
          </label>
          <label className="text-sm">
            <span className="block text-zinc-500 font-medium mb-1 uppercase tracking-wider text-xs">To</span>
            <input type="date" value={dateTo} max={todayIST()} onChange={(e) => setDateTo(e.target.value)} className="h-10 px-3 border-2 border-zinc-200 rounded-sm bg-white" />
          </label>
          <label className="text-sm">
            <span className="block text-zinc-500 font-medium mb-1 uppercase tracking-wider text-xs">Employee</span>
            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className="h-10 px-3 border-2 border-zinc-200 rounded-sm bg-white min-w-[200px]">
              <option value="">All field employees</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{`${e.name} (${e.employee_id})`}</option>)}
            </select>
          </label>
          <button onClick={download} className="h-10 px-4 ml-auto bg-green-600 hover:bg-green-700 text-white text-sm font-bold uppercase rounded-sm flex items-center gap-2" data-testid="download-report">
            <Download size={16} /> Download CSV
          </button>
        </div>
        <p className="text-xs text-zinc-500">
          Visits, travel and gaps are worked out from GPS points and each site&apos;s radius. GPS can be off by tens of metres,
          so times are estimates; &quot;before&quot; / &quot;after&quot; mean the arrival or departure happened while no location was recorded.
        </p>

        <div className="bg-white border-2 border-zinc-100 rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="report-table">
            <thead className="bg-zinc-50 border-b-2 border-zinc-100">
              <tr>{report.columns.map(([h]) => <th key={h} className="text-left px-4 py-3 text-xs font-bold text-zinc-600 uppercase tracking-wider">{h}</th>)}</tr>
            </thead>
            <tbody>
              {(loading || result.type !== type) && <tr><td colSpan={report.columns.length} className="text-center py-10 text-zinc-500">Loading…</td></tr>}
              {!loading && rows?.length === 0 && <tr><td colSpan={report.columns.length} className="text-center py-10 text-zinc-500">No field duty in this period.</td></tr>}
              {!loading && rows?.map((r, i) => (
                <tr key={i} className="border-b border-zinc-100">
                  {report.columns.map(([h, value]) => <td key={h} className="px-4 py-2.5">{value(r)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
